/**
 * Sitemap Health Card
 *
 * Displays sitemap health status including parsing errors and coverage stats.
 * Part of the Robot Diagnostics dashboard.
 *
 * Story: Sitemap Health Diagnostics
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Loader2,
  FileText,
  CheckCircle,
  AlertTriangle,
  XCircle,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Play,
} from 'lucide-react';
import {
  getSitemapHealth,
  testSitemap,
  SitemapHealthData,
  getSitemapStatusBadgeColor,
} from '@/services/robotDiagnosticsService';
import { formatDistanceToNow } from 'date-fns';

interface SitemapHealthCardProps {
  siteId: string;
}

export function SitemapHealthCard({ siteId }: SitemapHealthCardProps) {
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [data, setData] = useState<SitemapHealthData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorsExpanded, setErrorsExpanded] = useState(false);

  const loadData = useCallback(async () => {
    if (!siteId) return;

    setLoading(true);
    setError(null);

    try {
      const result = await getSitemapHealth(siteId);
      setData(result);
    } catch (err) {
      console.error('Failed to load sitemap health:', err);
      setError('Failed to load sitemap health');
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleTestSitemap = async () => {
    if (!siteId) return;

    setTesting(true);
    setError(null);

    try {
      await testSitemap(siteId);
      // Reload data after test
      await loadData();
    } catch (err) {
      console.error('Failed to test sitemap:', err);
      setError('Failed to test sitemap');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Sitemap Health
          </CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const status = data?.latestTest?.status;
  const hasErrors = data?.latestTest?.errors && data.latestTest.errors.length > 0;

  // Determine banner color and icon based on status
  const getBannerStyle = () => {
    switch (status) {
      case 'success':
        return {
          bg: 'bg-green-50 dark:bg-green-950/40 border-green-200 dark:border-green-800',
          icon: <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />,
          text: 'Sitemap is healthy',
        };
      case 'partial':
        return {
          bg: 'bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800',
          icon: <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />,
          text: 'Sitemap has issues but URLs were found',
        };
      case 'failed':
        return {
          bg: 'bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-800',
          icon: <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />,
          text: 'Sitemap failed to parse',
        };
      default:
        return {
          bg: 'bg-gray-50 dark:bg-gray-900/40 border-gray-200 dark:border-gray-700',
          icon: <FileText className="h-5 w-5 text-gray-600 dark:text-gray-400" />,
          text: 'No sitemap test results',
        };
    }
  };

  const bannerStyle = getBannerStyle();

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Sitemap Health
            </CardTitle>
            <CardDescription>
              Check sitemap accessibility and parsing status
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={loadData}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              size="sm"
              onClick={handleTestSitemap}
              disabled={testing}
            >
              {testing ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Play className="h-4 w-4 mr-1" />
              )}
              {testing ? 'Testing...' : 'Test Sitemap'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Error Alert */}
        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Status Banner */}
        <div className={`p-3 rounded-lg border ${bannerStyle.bg}`}>
          <div className="flex items-center gap-2">
            {bannerStyle.icon}
            <span className="font-medium">{bannerStyle.text}</span>
            {status && (
              <Badge className={getSitemapStatusBadgeColor(status)}>
                {status}
              </Badge>
            )}
          </div>
        </div>

        {/* Sitemap URL */}
        {data?.sitemapUrl && (
          <div>
            <div className="text-xs font-medium text-muted-foreground uppercase mb-1">
              Sitemap URL
            </div>
            <a
              href={data.sitemapUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm flex items-center gap-1 text-blue-600 hover:underline"
            >
              {data.sitemapUrl.length > 50
                ? data.sitemapUrl.substring(0, 50) + '...'
                : data.sitemapUrl}
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center p-3 bg-gray-50 dark:bg-gray-900/40 rounded-lg">
            <div className="text-2xl font-bold">
              {data?.latestTest?.urlsFound ?? data?.inventory?.totalUrls ?? '-'}
            </div>
            <div className="text-xs text-muted-foreground">URLs Found</div>
          </div>
          <div className="text-center p-3 bg-gray-50 dark:bg-gray-900/40 rounded-lg">
            <div className="text-2xl font-bold">
              {data?.inventory?.coveragePct ?? '-'}
              {data?.inventory?.coveragePct !== undefined ? '%' : ''}
            </div>
            <div className="text-xs text-muted-foreground">Coverage</div>
          </div>
          <div className="text-center p-3 bg-gray-50 dark:bg-gray-900/40 rounded-lg">
            <div className="text-2xl font-bold">
              {data?.inventory?.uncrawled ?? '-'}
            </div>
            <div className="text-xs text-muted-foreground">Uncrawled</div>
          </div>
        </div>

        {/* Last Test Info */}
        {data?.latestTest && (
          <div className="text-xs text-muted-foreground">
            Last tested{' '}
            {formatDistanceToNow(new Date(data.latestTest.testedAt), {
              addSuffix: true,
            })}
            {data.latestTest.durationMs && (
              <span> ({data.latestTest.durationMs}ms)</span>
            )}
          </div>
        )}

        {/* Errors Section */}
        {hasErrors && (
          <div className="border rounded-lg overflow-hidden">
            <button
              className="w-full p-3 flex items-center justify-between bg-red-50 dark:bg-red-950/40 text-left"
              onClick={() => setErrorsExpanded(!errorsExpanded)}
            >
              <div className="flex items-center gap-2 text-red-700 dark:text-red-300">
                <AlertTriangle className="h-4 w-4" />
                <span className="font-medium">
                  {data.latestTest!.errors.length} Parsing Error
                  {data.latestTest!.errors.length > 1 ? 's' : ''} Found
                </span>
              </div>
              {errorsExpanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </button>

            {errorsExpanded && (
              <div className="p-3 space-y-3 bg-white dark:bg-slate-800">
                {data.latestTest!.errors.map((err, index) => (
                  <div
                    key={index}
                    className="p-3 rounded bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800"
                  >
                    <div className="flex items-start gap-2">
                      <XCircle className="h-4 w-4 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-red-800 dark:text-red-200">
                          {err.type === 'xml_malformed'
                            ? 'Malformed XML'
                            : err.type === 'fetch_error'
                            ? 'Fetch Error'
                            : err.type === 'xml_parse_error'
                            ? 'XML Parse Error'
                            : 'Parse Error'}
                        </div>
                        <div className="text-sm text-red-700 dark:text-red-300 mt-1">
                          {err.message}
                        </div>
                        {err.url && (
                          <a
                            href={err.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-red-600 dark:text-red-400 hover:underline flex items-center gap-1 mt-1"
                          >
                            {err.url.length > 60
                              ? err.url.substring(0, 60) + '...'
                              : err.url}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* No Test Results Yet */}
        {!data?.latestTest && !loading && (
          <div className="text-center py-4 text-muted-foreground">
            <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No sitemap test results yet</p>
            <p className="text-xs mt-1">
              Click "Test Sitemap" to check for parsing issues
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
